import { ChallengeKind, DEFAULT_STAR_HP_PERCENT, LevelChallenge, LevelData } from "../data/balance";
import { Command, CommandKind } from "./command";
import { LevelPhase, LevelSim } from "./levelSim";

/**
 * Level stars (ENGAGEMENT §A1–A2, §B1). ★1 for the win; ★2 for a fortress at the level's `starHpPercent` of its HP or
 * more at the end (D15: 50 % on the teaching levels 1–10, 75 % from 11); ★3 for the level's challenge
 * (`LevelData.challenge`). All three are judged after the level, from the final sim state and the tally of the accepted
 * commands — nothing here runs inside the simulation, so the sim hash and the goldens never see it. A player keeps
 * every star condition ever met on a level (a bit mask, `StarMask`), so a challenge run that dips under the ★2 line
 * still banks its ★3.
 */

/** Percentages are compared as `hp × 100 ≥ pct × max`, in integers, so no float rounding decides a star. */
const PERCENT = 100;

/** ★2's default threshold (percent), for a level that sets none (`levels.json` → `starHpPercent`). */
export const STAR_HP_PERCENT = DEFAULT_STAR_HP_PERCENT;

/** Whether a fortress at `hp` of `maxHp` meets the level's ★2 line (integers, so no float rounding decides it). */
export function keepsStarHp(level: LevelData, hp: number, maxHp: number): boolean { return hp * PERCENT >= level.starHpPercent * maxHp; }

/** Stars a level can give. */
export const STARS_PER_LEVEL = 3;
/** Bit per star condition in a `StarMask`: win, HP, challenge. */
export const STAR_WIN = 1, STAR_HP = 2, STAR_CHALLENGE = 4;
/** Every condition met. */
export const STAR_ALL = STAR_WIN | STAR_HP | STAR_CHALLENGE;

/** A set of star conditions as bits (STAR_WIN | STAR_HP | STAR_CHALLENGE). */
export type StarMask = number;

/** How many stars a mask holds. */
export function starCount(mask: StarMask): number {
  return (mask & STAR_WIN ? 1 : 0) + (mask & STAR_HP ? 1 : 0) + (mask & STAR_CHALLENGE ? 1 : 0);
}


/**
 * What a run did, for the challenge: a tally of the accepted commands. `note` must see every accepted command, right
 * after it ran (the tower a placement made is read off the sim then); LevelSession does that. A replay rebuilds the
 * same tally by feeding the same commands through `note`.
 */
export class RunTally {
  /** The archetype of every tower placed, in order (a sold tower stays listed). */
  readonly built: number[] = [];
  upgrades = 0;
  rerolls = 0;
  roadCards = 0;
  skips = 0;
  earlyCalls = 0;

  note(sim: LevelSim, command: Command): void {
    switch (command.kind) {
      case CommandKind.PlaceTower: {
        // placeTower hands out ids in order, so the tower just placed holds the newest one.
        const t = sim.findTower(sim.nextTowerId - 1);
        if (t) this.built.push(t.archetype);
        return;
      }
      case CommandKind.UpgradeTower: this.upgrades++; return;
      case CommandKind.Reroll: this.rerolls++; return;
      case CommandKind.PlaceSegment: this.roadCards++; return;
      case CommandKind.SkipOffer: this.skips++; return;
      case CommandKind.CallNextStage: this.earlyCalls++; return;
      default: return;
    }
  }
}

/** Whether a finished run met its challenge (a lost run never does). */
export function challengeMet(challenge: LevelChallenge | null, sim: LevelSim, tally: RunTally): boolean {
  if (challenge === null || sim.phase !== LevelPhase.Won) return false;
  if (challenge.kind === ChallengeKind.EarlyCalls) return tally.earlyCalls >= challenge.n;
  return challengeKept(challenge, sim, tally);
}

/**
 * Whether the run still keeps the challenge, mid-level (the reference player asks before each command): the counting
 * kinds can only break, and MinHp breaks for good once the fortress drops under it (HP only ever falls). EarlyCalls is
 * decided at the end, so it reads as kept until then.
 */
export function challengeKept(challenge: LevelChallenge | null, sim: LevelSim, tally: RunTally): boolean {
  if (challenge === null) return false;
  const n = challenge.n;
  switch (challenge.kind) {
    case ChallengeKind.MaxTowers: return tally.built.length <= n;
    case ChallengeKind.NoTower: return !tally.built.includes(n);
    case ChallengeKind.MinHp: return sim.baseHp * PERCENT >= n * sim.maxBaseHp;
    case ChallengeKind.MaxUpgrades: return tally.upgrades <= n;
    case ChallengeKind.NoReroll: return tally.rerolls === 0;
    case ChallengeKind.MaxRoad: return tally.roadCards <= n;
    case ChallengeKind.NoSkip: return tally.skips === 0;
    case ChallengeKind.EarlyCalls: return true;
    default: return false;
  }
}

/** The stars a finished run earned. */
export function runStars(sim: LevelSim, tally: RunTally): StarMask {
  if (sim.phase !== LevelPhase.Won) return 0;
  let mask = STAR_WIN;
  if (keepsStarHp(sim.level, sim.baseHp, sim.maxBaseHp)) mask |= STAR_HP;
  if (challengeMet(sim.level.challenge, sim, tally)) mask |= STAR_CHALLENGE;
  return mask;
}
